/**
 * Friendly messages for v2 report run failures. fetchWithAuth throws an Error
 * carrying the HTTP `status` (and the parsed `errorBody`); the API's own
 * messages are mostly written for developers (e.g. "Unknown filter: 'x'"), so
 * the canvas shows these instead. The one exception is a 403, whose message
 * is written for the person who was refused.
 */

export type RunErrorKind =
  | "bad_request"
  | "forbidden"
  | "not_found"
  | "too_large"
  | "timeout"
  | "unknown";

export interface RunError {
  kind: RunErrorKind;
  /** HTTP status when known. */
  status?: number;
  title: string;
  message: string;
  /** False when running again can't help (403, 404): no "Try again". */
  retryable: boolean;
}

const MESSAGES: Record<RunErrorKind, { title: string; message: string }> = {
  bad_request: {
    title: "This view can't run",
    message:
      "One of this view's settings isn't supported. Try a different date range, filter or grouping.",
  },
  forbidden: {
    title: "Report not available",
    message:
      "This report shows location-wide totals and isn't available to staff whose report access is limited to their own data.",
  },
  not_found: {
    title: "Report not available",
    message:
      "This report couldn't be found. It may have been removed from your list.",
  },
  too_large: {
    title: "Too many rows to show",
    message:
      "This report has too many rows. Narrow the date range or remove a grouping.",
  },
  timeout: {
    title: "This report took too long",
    message: "The report took too long to run. Try a shorter date range.",
  },
  unknown: {
    title: "Couldn't load this report",
    message: "Something went wrong running this report. Try again in a moment.",
  },
};

const kindForStatus = (status: number | undefined): RunErrorKind => {
  switch (status) {
    case 400:
      return "bad_request";
    // The API refuses a v2 run for staff whose reports scope is "own" (the
    // definition says so with `supports_own_scope: false`).
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 422:
      return "too_large";
    // 503 is the API's own statement timeout; API Gateway answers 504 when
    // its integration timeout fires first.
    case 503:
    case 504:
      return "timeout";
    default:
      return "unknown";
  }
};

/** The HTTP status fetchWithAuth attached to a thrown error, if any. */
export const httpStatusOf = (error: unknown): number | undefined =>
  error && typeof error === "object" && "status" in error
    ? Number((error as { status?: unknown }).status) || undefined
    : undefined;

/** The API's own `message` from a JSON error body, if it sent one. */
const apiMessageOf = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object" || !("errorBody" in error)) {
    return undefined;
  }
  const body = (error as { errorBody?: unknown }).errorBody;
  const message =
    body && typeof body === "object"
      ? (body as { message?: unknown }).message
      : undefined;
  return typeof message === "string" && message.trim()
    ? message.trim()
    : undefined;
};

const NOT_RETRYABLE: ReadonlySet<RunErrorKind> = new Set<RunErrorKind>([
  "forbidden",
  "not_found",
]);

export function toRunError(error: unknown): RunError {
  const status = httpStatusOf(error);
  const kind = kindForStatus(status);
  const copy = MESSAGES[kind];
  // A 403 explains who may run the report; prefer the API's wording.
  const message =
    kind === "forbidden" ? (apiMessageOf(error) ?? copy.message) : copy.message;
  return {
    kind,
    status,
    title: copy.title,
    message,
    retryable: !NOT_RETRYABLE.has(kind),
  };
}

/** True for a fetch cancelled through an AbortController. */
export const isAbortError = (error: unknown): boolean =>
  !!error &&
  typeof error === "object" &&
  (error as { name?: unknown }).name === "AbortError";
